import { Avatar, Button, Space, Tooltip } from 'antd';
import { EyeOutlined, EditOutlined } from '@ant-design/icons';
import type { TableProps } from 'antd';
import { StatusTag } from '../../../components/StatusTag';
import { tonoEstadoServicio } from '../../../lib/estado-servicio-tono';
import { getInitials } from '../../../lib/initials';
import type { ServicioResponse } from '@aid/shared';
import { serverSortProps, type ServerSortState } from '../../../lib/server-sort';
import type { ServiciosGroupBy } from '../servicios-agrupacion';

type Translate = (key: string, options?: Record<string, unknown>) => string;
type Columns = TableProps<ServicioResponse>['columns'];

interface ServiciosColumnsDeps {
  t: Translate;
  token: ReturnType<typeof import('antd').theme.useToken>['token'];
  canUpdate: boolean;
  onVer: (id: number) => void;
  onEditar: (id: number) => void;
  groupBy: ServiciosGroupBy;
  sort: ServerSortState;
}

export function buildServiciosColumns({
  t,
  token,
  canUpdate,
  onVer,
  onEditar,
  groupBy,
  sort,
}: ServiciosColumnsDeps): Columns {
  const columns: NonNullable<Columns> = [
    {
      title: t('pages.servicios.cliente'),
      key: 'cliente',
      ...serverSortProps('cliente', sort),
      render: (_: unknown, record: ServicioResponse) => (
        <span>
          <Avatar
            size={24}
            style={{
              background: token.colorPrimary,
              verticalAlign: 'middle',
              marginRight: 8,
              fontSize: 12,
            }}
            aria-hidden
          >
            {getInitials(record.cliente?.nombre)}
          </Avatar>
          {record.cliente?.nombre ?? `#${record.idCliente}`}
        </span>
      ),
    },
    {
      title: t('pages.servicios.plan'),
      key: 'plan',
      ...serverSortProps('plan', sort),
      render: (_: unknown, record: ServicioResponse) => record.plan?.nombre ?? `#${record.idPlan}`,
    },
    {
      title: t('pages.servicios.tipo'),
      key: 'tipoServicio',
      ...serverSortProps('tipoServicio', sort),
      render: (_: unknown, record: ServicioResponse) =>
        record.tipoServicio?.nombre ?? `#${record.idTipoServicio}`,
    },
    {
      title: t('pages.servicios.status'),
      key: 'estado',
      ...serverSortProps('estado', sort),
      render: (_: unknown, record: ServicioResponse) => {
        const est = record.estado;
        return est ? (
          <StatusTag tone={tonoEstadoServicio(est.key)}>
            {est.value ?? est.key ?? `#${est.id}`}
          </StatusTag>
        ) : (
          <StatusTag tone="info">-</StatusTag>
        );
      },
    },
    {
      title: t('pages.servicios.direccion'),
      dataIndex: 'direccion' as keyof ServicioResponse,
      key: 'direccion',
      width: 300,
      render: (val: string | null) =>
        val ? (
          <Tooltip title={val}>
            <div
              style={{
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
              }}
            >
              {val}
            </div>
          </Tooltip>
        ) : (
          <span style={{ color: token.colorTextTertiary }}>-</span>
        ),
    },
    {
      title: t('common.actions'),
      key: 'actions',
      width: 100,
      render: (_: unknown, record: ServicioResponse) => (
        <Space size={4}>
          <Tooltip title={t('common.ver')}>
            <Button
              type="text"
              icon={<EyeOutlined />}
              aria-label={t('common.ver')}
              onClick={() => onVer(record.id)}
            />
          </Tooltip>
          {canUpdate && (
            <Tooltip title={t('common.edit')}>
              <Button
                type="text"
                icon={<EditOutlined />}
                aria-label={t('common.edit')}
                onClick={() => onEditar(record.id)}
              />
            </Tooltip>
          )}
        </Space>
      ),
    },
  ];

  return columns.filter((col) => {
    if (groupBy === 'cliente' && col.key === 'cliente') return false;
    if (groupBy === 'estado' && col.key === 'estado') return false;
    return true;
  });
}
